/**
 * Patient Chart Page
 *
 * Sub-tabs: History Log (default) | Documents | Snapshots
 * Patient header with name, MRN, internal ID, stats.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Camera,
  Clock,
  Upload,
  Plus,
  Stethoscope,
  Pill,
  Activity,
  AlertCircle,
  Play,
  Loader2,
  ClipboardList,
  AlertTriangle,
  RefreshCw,
  Trash2,
  PanelRightOpen,
  PanelRightClose,
  X,
} from 'lucide-react';
import * as mhrApi from '../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { MedicalHistoryLog } from '../components/HistoryLog/MedicalHistoryLog';
import { StrikeOutReasonModal } from '../components/HistoryLog/StrikeOutReasonModal';
import { SnapshotButton } from '../components/HistoryLog/SnapshotButton';
import { DeletePatientModal } from '../components/DeletePatientModal';
import { PDFViewer, PageNavigation, ZoomControls } from '../components/DocumentViewer';
import {
  DropZone,
  BatchUploadList,
  AsyncProcessingStatus,
  CostEstimateModal,
} from '../components/Upload';
import { MedicalRecordView } from '../components/MedicalRecord';
import type { HistoryEntry, HistoryEntryType } from '../components/HistoryLog/HistoryEntryCard';
import type {
  MHRPatient,
  MHRDocument,
  UploadFile,
  CostEstimate,
  HistoryLog,
  HistoryDiagnosis,
  HistoryMedication,
  HistoryProcedure,
  HistorySnapshot,
  Review,
  Protocol,
  MergeSuggestion,
  MedDxLink,
} from '../types';

type Tab = 'history' | 'documents' | 'snapshots';

export function PatientChart() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === 'super_admin' || user?.role === 'administrator';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('history');

  // Data
  const [patient, setPatient] = useState<MHRPatient | null>(null);
  const [historyLog, setHistoryLog] = useState<HistoryLog | null>(null);
  const [documents, setDocuments] = useState<MHRDocument[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [diagnoses, setDiagnoses] = useState<HistoryDiagnosis[]>([]);
  const [medications, setMedications] = useState<HistoryMedication[]>([]);
  const [procedures, setProcedures] = useState<HistoryProcedure[]>([]);
  const [snapshots, setSnapshots] = useState<HistorySnapshot[]>([]);
  const [mergeSuggestions, setMergeSuggestions] = useState<MergeSuggestion[]>([]);
  const [medDxLinks, setMedDxLinks] = useState<MedDxLink[]>([]);
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState('');
  const [startingReview, setStartingReview] = useState(false);

  // Upload state (embedded in Documents tab)
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [asyncProcessingDocs, setAsyncProcessingDocs] = useState<Set<string>>(new Set());
  const [processingDocId, setProcessingDocId] = useState<string | null>(null);
  const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [confirmingCost, setConfirmingCost] = useState(false);
  const [reprocessingDocId, setReprocessingDocId] = useState<string | null>(null);

  // Delete patient state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Document viewer panel state
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerPage, setDocViewerPage] = useState(1);
  const [docViewerZoom, setDocViewerZoom] = useState(1.0);
  const [docViewerTotalPages, setDocViewerTotalPages] = useState(0);
  const [docViewerDocId, setDocViewerDocId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [pdfLoading, setPdfLoading] = useState(false);

  // Strike-out modal state
  const [strikeOutTarget, setStrikeOutTarget] = useState<{ entry: HistoryEntry; entryType: HistoryEntryType } | null>(null);

  // Load chart data
  const loadChart = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const [chart, protocolsData] = await Promise.all([
        mhrApi.getPatientChart(patientId),
        mhrApi.listProtocols({ status: 'active' }).catch(() => ({ data: [], count: 0 })),
      ]);
      setPatient(chart.patient);
      setHistoryLog(chart.history_log);
      setDocuments(chart.documents);
      setReviews(chart.recent_reviews);
      setProtocols(protocolsData.data || []);

      // Load history log entries and snapshots if log exists
      if (chart.history_log) {
        try {
          const logData = await mhrApi.getHistoryLog(patientId);
          setDiagnoses(logData.diagnoses);
          setMedications(logData.medications);
          setProcedures(logData.procedures);
        } catch {
          // Log may not have entries yet
        }

        try {
          const snaps = await mhrApi.getSnapshots(chart.history_log.id);
          setSnapshots(snaps);
        } catch {
          // Snapshots may not exist yet
        }

        try {
          const suggestions = await mhrApi.getMergeSuggestions(chart.history_log.id);
          setMergeSuggestions(suggestions.filter((s: MergeSuggestion) => s.status === 'pending'));
        } catch {
          // Merge suggestions are optional
        }

        try {
          const links = await mhrApi.getMedDxLinks(chart.history_log.id);
          setMedDxLinks(links);
        } catch {
          // Med-dx links are optional
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load patient chart');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  // Refresh just documents (lighter than full loadChart)
  const refreshDocuments = useCallback(async () => {
    if (!patientId) return;
    try {
      const docs = await mhrApi.listDocuments(patientId);
      setDocuments(docs);
    } catch {
      // Fall back to full reload
      loadChart();
    }
  }, [patientId, loadChart]);

  // Create history log
  const handleCreateLog = async () => {
    if (!patientId) return;
    try {
      await mhrApi.createHistoryLog(patientId);
      await loadChart();
    } catch (err: any) {
      if (err.response?.status === 409) {
        // Already exists, reload
        loadChart();
      } else {
        setError(err.response?.data?.error || 'Failed to create history log');
      }
    }
  };

  const handleStartReview = async () => {
    if (!historyLog || !selectedProtocol) return;
    setStartingReview(true);
    try {
      const result = await mhrApi.startReviewFromHistoryLog(historyLog.id, selectedProtocol);
      if (result.success && result.reviewId) {
        navigate(`/mhr/reviews/${result.reviewId}`);
      } else if (result.requiresApproval) {
        setError(result.message || 'Budget approval required');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start review');
    } finally {
      setStartingReview(false);
      setShowReviewModal(false);
    }
  };

  // Generate unique ID for upload files
  const generateId = () => Math.random().toString(36).substring(2, 11);

  // Handle files selected from drop zone
  const handleFilesSelected = (files: File[]) => {
    const newUploadFiles: UploadFile[] = files.map(file => ({
      file,
      id: generateId(),
      status: 'pending',
      progress: 0,
    }));
    setUploadFiles(prev => [...prev, ...newUploadFiles]);
  };

  // Handle remove file from list
  const handleRemoveFile = (fileId: string) => {
    setUploadFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Handle upload all files
  const handleUploadAll = async () => {
    if (!patientId || uploadFiles.length === 0) return;
    setUploading(true);
    setError(null);
    const pendingFiles = uploadFiles.filter(f => f.status === 'pending');

    for (const uploadFile of pendingFiles) {
      setUploadFiles(prev =>
        prev.map(f => (f.id === uploadFile.id ? { ...f, status: 'uploading' as const } : f))
      );
      try {
        const document = await mhrApi.uploadDocument(patientId, uploadFile.file, progress => {
          setUploadFiles(prev =>
            prev.map(f => (f.id === uploadFile.id ? { ...f, progress } : f))
          );
        });
        setUploadFiles(prev =>
          prev.map(f =>
            f.id === uploadFile.id ? { ...f, status: 'success' as const, progress: 100, document } : f
          )
        );
        setDocuments(prev => [...prev, document]);
      } catch (err: any) {
        setUploadFiles(prev =>
          prev.map(f =>
            f.id === uploadFile.id
              ? { ...f, status: 'error' as const, error: err.response?.data?.error || 'Upload failed' }
              : f
          )
        );
      }
    }
    setUploading(false);
  };

  // Handle retry failed upload
  const handleRetryUpload = async (fileId: string) => {
    const uploadFile = uploadFiles.find(f => f.id === fileId);
    if (!uploadFile || !patientId) return;
    setUploadFiles(prev =>
      prev.map(f => (f.id === fileId ? { ...f, status: 'uploading' as const, error: undefined, progress: 0 } : f))
    );
    try {
      const document = await mhrApi.uploadDocument(patientId, uploadFile.file, progress => {
        setUploadFiles(prev => prev.map(f => (f.id === fileId ? { ...f, progress } : f)));
      });
      setUploadFiles(prev =>
        prev.map(f =>
          f.id === fileId ? { ...f, status: 'success' as const, progress: 100, document } : f
        )
      );
      setDocuments(prev => [...prev, document]);
    } catch (err: any) {
      setUploadFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'error' as const, error: err.response?.data?.error || 'Upload failed' }
            : f
        )
      );
    }
  };

  // Handle get cost estimate
  const handleGetCostEstimate = async (documentId: string) => {
    setProcessingDocId(documentId);
    try {
      const estimate = await mhrApi.getCostEstimate(documentId);
      setCostEstimate(estimate);
      setShowCostModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to get cost estimate');
    }
  };

  // Handle confirm processing
  const handleConfirmProcessing = async () => {
    if (!processingDocId) return;
    setConfirmingCost(true);
    try {
      await mhrApi.startAsyncProcessing(processingDocId);
      setAsyncProcessingDocs(prev => new Set(prev).add(processingDocId));
      setShowCostModal(false);
      setCostEstimate(null);
      if (patientId) {
        const docs = await mhrApi.listDocuments(patientId);
        setDocuments(docs);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start processing');
    } finally {
      setConfirmingCost(false);
    }
  };

  // Handle async processing completion
  const handleAsyncProcessingComplete = useCallback((documentId: string) => {
    setAsyncProcessingDocs(prev => {
      const next = new Set(prev);
      next.delete(documentId);
      return next;
    });
    // Reload all chart data including history log entries
    loadChart();
  }, [loadChart]);

  // Handle merge suggestion accept/reject (optimistic for triage)
  const handleAcceptMerge = async (suggestion: MergeSuggestion) => {
    if (!historyLog) return;
    setMergeSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    try {
      await mhrApi.acceptMergeSuggestion(historyLog.id, suggestion.id);
    } catch (err: any) {
      // Re-add on failure
      setMergeSuggestions(prev => [...prev, suggestion]);
      setError(err.response?.data?.error || 'Failed to accept merge suggestion');
    }
  };

  const handleRejectMerge = async (suggestion: MergeSuggestion) => {
    if (!historyLog) return;
    setMergeSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    try {
      await mhrApi.rejectMergeSuggestion(historyLog.id, suggestion.id);
    } catch (err: any) {
      setMergeSuggestions(prev => [...prev, suggestion]);
      setError(err.response?.data?.error || 'Failed to reject merge suggestion');
    }
  };

  // Batch merge handlers
  const handleBatchAcceptMerge = async () => {
    if (!historyLog) return;
    const prev = mergeSuggestions;
    setMergeSuggestions([]);
    try {
      await mhrApi.batchAcceptMergeSuggestions(historyLog.id);
      loadChart();
    } catch (err: any) {
      setMergeSuggestions(prev);
      setError(err.response?.data?.error || 'Failed to batch accept merge suggestions');
    }
  };

  const handleBatchRejectMerge = async () => {
    if (!historyLog) return;
    const prev = mergeSuggestions;
    setMergeSuggestions([]);
    try {
      await mhrApi.batchRejectMergeSuggestions(historyLog.id);
    } catch (err: any) {
      setMergeSuggestions(prev);
      setError(err.response?.data?.error || 'Failed to batch reject merge suggestions');
    }
  };

  const handleTriageComplete = () => {
    loadChart();
  };

  // Inline edit handlers for compact grid
  const handleEditDiagnosis = async (id: string, field: string, value: any) => {
    if (!historyLog) return;
    setDiagnoses(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
    try {
      await mhrApi.updateHistoryDiagnosis(historyLog.id, id, { [field]: value });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update diagnosis');
      loadChart();
    }
  };

  const handleEditMedication = async (id: string, field: string, value: any) => {
    if (!historyLog) return;
    setMedications(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
    try {
      await mhrApi.updateHistoryMedication(historyLog.id, id, { [field]: value });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update medication');
      loadChart();
    }
  };

  const handleEditProcedure = async (id: string, field: string, value: any) => {
    if (!historyLog) return;
    setProcedures(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
    try {
      await mhrApi.updateHistoryProcedure(historyLog.id, id, { [field]: value });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update procedure');
      loadChart();
    }
  };

  // Verify entry
  const handleVerifyEntry = useCallback(async (entry: HistoryEntry, entryType: HistoryEntryType) => {
    if (!historyLog) return;
    const typeMap = { diagnosis: 'diagnoses', medication: 'medications', procedure: 'procedures' } as const;
    try {
      const result = await mhrApi.verifyHistoryEntry(historyLog.id, typeMap[entryType], entry.id);
      if (entryType === 'diagnosis') {
        setDiagnoses(prev => prev.map(d => d.id === entry.id ? { ...d, verified_by: result.verified_by, verified_at: result.verified_at } : d));
      } else if (entryType === 'medication') {
        setMedications(prev => prev.map(m => m.id === entry.id ? { ...m, verified_by: result.verified_by, verified_at: result.verified_at } : m));
      } else {
        setProcedures(prev => prev.map(p => p.id === entry.id ? { ...p, verified_by: result.verified_by, verified_at: result.verified_at } : p));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to verify entry');
    }
  }, [historyLog]);

  // Delete entry
  const handleDeleteEntry = useCallback(async (entry: HistoryEntry, entryType: HistoryEntryType) => {
    if (!historyLog) return;
    if (!window.confirm('Are you sure you want to permanently delete this entry?')) return;
    try {
      if (entryType === 'diagnosis') {
        await mhrApi.deleteHistoryDiagnosis(historyLog.id, entry.id);
        setDiagnoses(prev => prev.filter(d => d.id !== entry.id));
      } else if (entryType === 'medication') {
        await mhrApi.deleteHistoryMedication(historyLog.id, entry.id);
        setMedications(prev => prev.filter(m => m.id !== entry.id));
      } else {
        await mhrApi.deleteHistoryProcedure(historyLog.id, entry.id);
        setProcedures(prev => prev.filter(p => p.id !== entry.id));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete entry');
      loadChart();
    }
  }, [historyLog, loadChart]);

  // Handle reprocess document
  const handleReprocessDocument = async (documentId: string) => {
    setReprocessingDocId(documentId);
    try {
      await mhrApi.startAsyncProcessing(documentId);
      setAsyncProcessingDocs(prev => new Set(prev).add(documentId));
      if (patientId) {
        const docs = await mhrApi.listDocuments(patientId);
        setDocuments(docs);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reprocess document');
    } finally {
      setReprocessingDocId(null);
    }
  };

  // Handle delete patient
  const handleDeletePatient = async () => {
    if (!patientId) return;
    setDeleting(true);
    try {
      await mhrApi.deletePatient(patientId);
      navigate('/mhr');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete patient');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  // === Document viewer & strike-out handlers ===

  // Fetch document as blob URL when viewer opens or document changes
  useEffect(() => {
    if (!docViewerOpen || !docViewerDocId || pdfUrl) return;
    let cancelled = false;
    setPdfLoading(true);
    mhrApi.getDocumentViewUrl(docViewerDocId)
      .then(url => {
        if (!cancelled) setPdfUrl(url);
      })
      .catch(err => {
        console.error('[PatientChart] Error fetching document view URL:', err);
        if (!cancelled) setError('Failed to load document');
      })
      .finally(() => { if (!cancelled) setPdfLoading(false); });
    return () => { cancelled = true; };
  }, [docViewerOpen, docViewerDocId, pdfUrl]);

  // Revoke blob URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Get the currently viewed document info
  const viewerDocument = useMemo(
    () => documents.find(d => d.id === docViewerDocId),
    [documents, docViewerDocId]
  );

  // Collect pages with findings for the current document
  const pagesWithFindings = useMemo(() => {
    if (!docViewerDocId) return [];
    const pages = new Set<number>();
    const allEntries = [...diagnoses, ...medications, ...procedures];
    for (const entry of allEntries) {
      if (entry.source_pages) {
        for (const sp of entry.source_pages) {
          if (sp.document_id === docViewerDocId) {
            pages.add(sp.page);
          }
        }
      }
    }
    return Array.from(pages).sort((a, b) => a - b);
  }, [docViewerDocId, diagnoses, medications, procedures]);

  // Handle page link click from history entries
  const handlePageClick = useCallback((documentId: string, page: number) => {
    if (documentId !== docViewerDocId) {
      // Different doc — reset URL to trigger re-fetch
      setPdfUrl('');
      setDocViewerDocId(documentId);
    }
    setDocViewerPage(page);
    setDocViewerOpen(true);
  }, [docViewerDocId]);

  // Handle strike-out: show modal
  const handleStrikeOut = useCallback((entry: HistoryEntry, entryType: HistoryEntryType) => {
    setStrikeOutTarget({ entry, entryType });
  }, []);

  // Confirm strike-out with reason
  const handleConfirmStrikeOut = useCallback(async (reason: string) => {
    if (!strikeOutTarget || !historyLog) return;
    const { entry, entryType } = strikeOutTarget;
    setStrikeOutTarget(null);

    const updateData = {
      struck_out: true,
      struck_out_reason: reason,
      struck_out_by: user?.id,
      struck_out_at: new Date().toISOString(),
    };

    // Optimistic update
    if (entryType === 'diagnosis') {
      setDiagnoses(prev => prev.map(d => d.id === entry.id ? { ...d, ...updateData } : d));
      try {
        await mhrApi.updateHistoryDiagnosis(historyLog.id, entry.id, updateData);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to strike out diagnosis');
        loadChart();
      }
    } else if (entryType === 'medication') {
      setMedications(prev => prev.map(m => m.id === entry.id ? { ...m, ...updateData } : m));
      try {
        await mhrApi.updateHistoryMedication(historyLog.id, entry.id, updateData);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to strike out medication');
        loadChart();
      }
    } else if (entryType === 'procedure') {
      setProcedures(prev => prev.map(p => p.id === entry.id ? { ...p, ...updateData } : p));
      try {
        await mhrApi.updateHistoryProcedure(historyLog.id, entry.id, updateData);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to strike out procedure');
        loadChart();
      }
    }
  }, [strikeOutTarget, historyLog, user?.id, loadChart]);

  // Handle undo strike-out
  const handleUndoStrikeOut = useCallback(async (entry: HistoryEntry, entryType: HistoryEntryType) => {
    if (!historyLog) return;

    const clearData = {
      struck_out: false,
      struck_out_reason: null,
      struck_out_by: null,
      struck_out_at: null,
    };

    if (entryType === 'diagnosis') {
      setDiagnoses(prev => prev.map(d => d.id === entry.id ? { ...d, struck_out: false, struck_out_reason: undefined, struck_out_by: undefined, struck_out_at: undefined } : d));
      try {
        await mhrApi.updateHistoryDiagnosis(historyLog.id, entry.id, clearData as any);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to undo strike out');
        loadChart();
      }
    } else if (entryType === 'medication') {
      setMedications(prev => prev.map(m => m.id === entry.id ? { ...m, struck_out: false, struck_out_reason: undefined, struck_out_by: undefined, struck_out_at: undefined } : m));
      try {
        await mhrApi.updateHistoryMedication(historyLog.id, entry.id, clearData as any);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to undo strike out');
        loadChart();
      }
    } else if (entryType === 'procedure') {
      setProcedures(prev => prev.map(p => p.id === entry.id ? { ...p, struck_out: false, struck_out_reason: undefined, struck_out_by: undefined, struck_out_at: undefined } : p));
      try {
        await mhrApi.updateHistoryProcedure(historyLog.id, entry.id, clearData as any);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to undo strike out');
        loadChart();
      }
    }
  }, [historyLog, loadChart]);

  // Get entry name for strike-out modal
  const getEntryName = (entry: HistoryEntry, entryType: HistoryEntryType): string => {
    if (entryType === 'diagnosis') return (entry as any).condition_name;
    if (entryType === 'medication') return (entry as any).medication_name;
    return (entry as any).procedure_name;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Patient not found</h3>
        <Link to="/mhr" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
          Back to Patient Database
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'history' as Tab, label: 'History Log', icon: Stethoscope },
    { id: 'documents' as Tab, label: 'Medical Record', icon: FileText, count: documents.length },
    { id: 'snapshots' as Tab, label: 'Snapshots', icon: Camera, count: snapshots.length || undefined },
  ];

  return (
    <div className="space-y-6">
      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 dark:text-red-300 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">&times;</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate('/mhr')}
          className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mt-1"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
              {patient.first_name && patient.last_name
                ? `${patient.first_name} ${patient.last_name}`
                : patient.name || patient.internal_id}
            </h1>
            {historyLog && (
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                historyLog.status === 'complete' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                historyLog.status === 'in_progress' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {historyLog.status === 'in_progress' ? 'In Progress' : historyLog.status.charAt(0).toUpperCase() + historyLog.status.slice(1)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            {patient.dob && (
              <span>DOB: {new Date(patient.dob + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            )}
            {patient.mrn && <span>MRN: {patient.mrn}</span>}
            <span className="text-gray-400 dark:text-gray-500">{patient.internal_id}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {historyLog && protocols.length > 0 && documents.length > 0 && (
            <button
              onClick={() => setShowReviewModal(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] sm:min-h-0"
            >
              <Play size={16} />
              Start Review
            </button>
          )}
          {historyLog && (
            <SnapshotButton
              historyLog={historyLog}
              onCreateSnapshot={async (data) => {
                const snap = await mhrApi.createSnapshot(historyLog.id, data);
                setSnapshots(prev => [snap, ...prev]);
              }}
            />
          )}
          {canDelete && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="Delete patient profile"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Stats row */}
      {historyLog && (
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <FileText size={16} className="text-gray-400 dark:text-gray-500" />
            <span>{historyLog.document_count} documents</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <Stethoscope size={16} className="text-gray-400 dark:text-gray-500" />
            <span>{historyLog.diagnosis_count} diagnoses</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <Pill size={16} className="text-gray-400 dark:text-gray-500" />
            <span>{historyLog.medication_count} medications</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <Activity size={16} className="text-gray-400 dark:text-gray-500" />
            <span>{historyLog.procedure_count} procedures</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <nav className="flex gap-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setError(null);
                  }}
                  className={`flex items-center gap-2 py-3 border-b-2 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">{tab.count}</span>
                  )}
                </button>
              );
            })}
          </nav>
          {activeTab === 'history' && documents.length > 0 && (
            <button
              onClick={() => {
                if (!docViewerOpen && documents.length > 0 && !docViewerDocId) {
                  setDocViewerDocId(documents[0].id);
                }
                setDocViewerOpen(!docViewerOpen);
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                docViewerOpen
                  ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {docViewerOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
              {docViewerOpen ? 'Hide Document' : 'View Document'}
            </button>
          )}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'history' && (
        <div className={docViewerOpen ? 'flex h-[calc(100vh-6rem)] -mx-4 -mb-4' : ''}>
          <div className={docViewerOpen ? 'flex-1 min-w-0 overflow-y-auto px-4 pb-4' : ''}>
            {historyLog ? (
              <MedicalHistoryLog
                historyLog={historyLog}
                diagnoses={diagnoses}
                medications={medications}
                procedures={procedures}
                mergeSuggestions={mergeSuggestions}
                medDxLinks={medDxLinks}
                onRefresh={loadChart}
                onAcceptMerge={handleAcceptMerge}
                onRejectMerge={handleRejectMerge}
                onBatchAcceptMerge={handleBatchAcceptMerge}
                onBatchRejectMerge={handleBatchRejectMerge}
                onTriageComplete={handleTriageComplete}
                onVerifyEntry={handleVerifyEntry}
                onDeleteEntry={handleDeleteEntry}
                onEditDiagnosis={handleEditDiagnosis}
                onEditMedication={handleEditMedication}
                onEditProcedure={handleEditProcedure}
                onStrikeOut={handleStrikeOut}
                onUndoStrikeOut={handleUndoStrikeOut}
                onPageClick={handlePageClick}
              />
            ) : (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <Stethoscope className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No History Log Yet</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Create a history log to start building this patient's medical history.
                </p>
                <button
                  onClick={handleCreateLog}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] sm:min-h-0"
                >
                  <Plus size={18} />
                  Create History Log
                </button>
              </div>
            )}
          </div>

          {/* Document viewer panel */}
          {docViewerOpen && (
            <div className="w-[50%] flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col">
              {/* Panel header */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText size={16} className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
                  {documents.length > 1 ? (
                    <select
                      value={docViewerDocId || ''}
                      onChange={e => {
                        setPdfUrl('');
                        setDocViewerDocId(e.target.value);
                        setDocViewerPage(1);
                      }}
                      className="text-sm font-medium text-gray-900 dark:text-gray-100 bg-transparent border-none cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 py-0.5 truncate max-w-[200px] focus:outline-none focus:ring-1 focus:ring-primary-400"
                    >
                      {documents.map(doc => (
                        <option key={doc.id} value={doc.id}>{doc.filename}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {viewerDocument?.filename || 'Document'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <ZoomControls zoom={docViewerZoom} onZoomChange={setDocViewerZoom} />
                  <button
                    onClick={() => setDocViewerOpen(false)}
                    className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    aria-label="Close document panel"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* PDF viewer */}
              <div className="flex-1 overflow-hidden relative">
                {pdfLoading ? (
                  <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900/40">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Loader2 className="animate-spin" size={20} />
                      <span className="text-sm">Loading document...</span>
                    </div>
                  </div>
                ) : pdfUrl ? (
                  <PDFViewer
                    documentUrl={pdfUrl}
                    currentPage={docViewerPage}
                    onPageChange={setDocViewerPage}
                    onLoadSuccess={setDocViewerTotalPages}
                    zoom={docViewerZoom}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900/40">
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No document available</p>
                  </div>
                )}
              </div>

              {/* Page navigation footer */}
              <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 flex-shrink-0">
                <PageNavigation
                  currentPage={docViewerPage}
                  totalPages={docViewerTotalPages || viewerDocument?.page_count || 0}
                  onPageChange={setDocViewerPage}
                  pagesWithFindings={pagesWithFindings}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <MedicalRecordView
          documents={documents}
          patientId={patientId!}
          onDocumentsChange={refreshDocuments}
          uploadFiles={uploadFiles}
          uploading={uploading}
          onFilesSelected={handleFilesSelected}
          onRemoveFile={handleRemoveFile}
          onRetryUpload={handleRetryUpload}
          onUploadAll={handleUploadAll}
          asyncProcessingDocs={asyncProcessingDocs}
          onGetCostEstimate={handleGetCostEstimate}
          onAsyncProcessingComplete={handleAsyncProcessingComplete}
          onReprocessDocument={handleReprocessDocument}
          reprocessingDocId={reprocessingDocId}
          onViewDocument={(docId, page) => {
            setDocViewerDocId(docId);
            setDocViewerOpen(true);
            if (page) setDocViewerPage(page);
          }}
        />
      )}

      {/* Cost Estimate Modal */}
      {costEstimate && (
        <CostEstimateModal
          estimate={costEstimate}
          onConfirm={handleConfirmProcessing}
          onCancel={() => {
            setShowCostModal(false);
            setCostEstimate(null);
            setProcessingDocId(null);
          }}
          isOpen={showCostModal}
          isSubmitting={confirmingCost}
        />
      )}

      {activeTab === 'snapshots' && (
        snapshots.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <Camera className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Snapshots</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Create a snapshot to capture a point-in-time view of the patient's history log.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {snapshots.map(snap => (
              <div
                key={snap.id}
                onClick={() => navigate(`/mhr/patients/${patientId}/snapshots/${snap.id}`)}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                  <Camera size={20} className="text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{snap.snapshot_name}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    <span>{snap.diagnosis_count} dx</span>
                    <span>{snap.medication_count} meds</span>
                    <span>{snap.procedure_count} procs</span>
                    <span className="capitalize">{snap.snapshot_type.replace('_', ' ')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                  <Clock size={14} />
                  {new Date(snap.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Delete Patient Modal */}
      <DeletePatientModal
        isOpen={showDeleteModal}
        patientName={
          patient.first_name && patient.last_name
            ? `${patient.first_name} ${patient.last_name}`
            : patient.name || patient.internal_id
        }
        onConfirm={handleDeletePatient}
        onCancel={() => setShowDeleteModal(false)}
        isLoading={deleting}
      />

      {/* Strike Out Reason Modal */}
      <StrikeOutReasonModal
        isOpen={!!strikeOutTarget}
        entryName={strikeOutTarget ? getEntryName(strikeOutTarget.entry, strikeOutTarget.entryType) : ''}
        onConfirm={handleConfirmStrikeOut}
        onCancel={() => setStrikeOutTarget(null)}
      />

      {/* Start Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReviewModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Start Protocol Review</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Review this patient's medical history against a protocol's inclusion/exclusion criteria.
              A pre-review snapshot will be created automatically.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Protocol</label>
              <select
                value={selectedProtocol}
                onChange={e => setSelectedProtocol(e.target.value)}
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-150"
              >
                <option value="">Choose a protocol...</option>
                {protocols.map(p => (
                  <option key={p.id} value={p.id}>{p.protocol_number}{p.protocol_title ? ` - ${p.protocol_title}` : ''}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg min-h-[44px] sm:min-h-0"
              >
                Cancel
              </button>
              <button
                onClick={handleStartReview}
                disabled={!selectedProtocol || startingReview}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
              >
                <Play size={16} />
                {startingReview ? 'Starting...' : 'Start Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
